import { useRouterState } from '@tanstack/react-router';
import { useState, useSyncExternalStore } from 'react';
import { OLD_FRONTEND_URL } from '@/lib/constants/api';
import { buildOldFrontendUrl, getStoredSession } from '@/lib/utils/auth';
import { trackAction } from '@/lib/utils/datadog';
import { hasOldFrontend } from '@/lib/utils/env';
import {
  canAccessManagement,
  MANAGEMENT_ROOT,
} from '@/lib/utils/managementAccess';
import {
  isOldDashboardBannerDismissed,
  setOldDashboardBannerDismissed,
  subscribeOldDashboardBannerDismiss,
} from '@/lib/utils/oldDashboardBannerState';
import { useSessionStore } from '@/store/sessionStore';

/** Mirrors the old frontend's `frontend_v2_try_now_clicked`, so the two
 *  directions can be compared in one Datadog dashboard. */
const OPT_OUT_ACTION = 'frontend_v2_opt_out_clicked';
const DISMISS_ACTION = 'frontend_v2_opt_out_dismissed';

/** Datadog's addAction is fire-and-forget; a full-page navigation can tear the
 *  document down before the SDK flushes. The old frontend waits the same 150ms
 *  before `window.location.assign`. */
const RUM_FLUSH_MS = 150;

/** Tells the old dashboard this return was deliberate, so it downgrades the
 *  stored opt-in instead of forwarding the user straight back here. Without it,
 *  leaving is impossible for anyone who has opted in. */
const OPT_OUT_PARAM = 'optOut';

function isImpersonating(): boolean {
  try {
    return Boolean(
      JSON.parse(localStorage.getItem('retraced:context') ?? '{}')?.state
        ?.impersonateId,
    );
  } catch {
    return false;
  }
}

/**
 * Visibility + navigation for the "back to the old dashboard" banner.
 *
 * The session travels with the user. That used to be unnecessary — everyone
 * arrived here from the old dashboard, so it already had their session — but v2
 * now has a login of its own, and a user who signed in here has no session on
 * the other origin. Without the hand-off they would land on the old login and
 * have to authenticate a second time.
 */
export function useOldDashboardBanner() {
  const user = useSessionStore(s => s.user);
  const pathname = useRouterState({ select: s => s.location.pathname });
  const [leaving, setLeaving] = useState(false);

  const dismissed = useSyncExternalStore(
    subscribeOldDashboardBannerDismiss,
    isOldDashboardBannerDismissed,
    () => true, // SSR/first paint: assume dismissed so the banner never flashes
  );

  // `hasOldFrontend`, not a truthy `OLD_FRONTEND_URL`: that constant falls
  // back to a dev URL when unset and, on a v2-only deployment such as
  // Zeeman, is configured to this app's own origin — so it is truthy in
  // exactly the two cases where there is nowhere to go back to.
  const visible = hasOldFrontend && !!user?.id && !dismissed;

  /**
   * Client pages only.
   *
   * These actions measure whether customers are adopting the new dashboard.
   * Staff moving between two internal tool sets is not that, so management
   * pages are left out rather than inflating every number built on them —
   * the same reasoning that keeps the old dashboard's `frontend_v2_*` actions
   * off its own `/management` pages.
   */
  const tracked = !pathname.startsWith(MANAGEMENT_ROOT);

  function trackedContext() {
    return {
      userId: user?.id,
      companyId: user?.company?.id,
      impersonating: isImpersonating(),
      pathname,
    };
  }

  async function handleLeave() {
    if (leaving) return;
    setLeaving(true);
    if (tracked) {
      trackAction(OPT_OUT_ACTION, trackedContext());
      // The flush wait only earns its stall when there is an action to flush.
      await new Promise(resolve => setTimeout(resolve, RUM_FLUSH_MS));
    }

    // Same encrypted `#token=` envelope the old dashboard already consumes
    // (`seedSessionFromHash`), carrying impersonation and company so the trip
    // resumes the same context. Falls back to a bare navigation when there is
    // no session to pass — the old dashboard's own guard takes it from there.
    // Staff came here for the management tools, so leaving puts them on the old
    // dashboard's own management area rather than its root — the same page
    // `/management` opens on this side. Everyone else lands on the root, which
    // is where they were before.
    const path = canAccessManagement(user)
      ? `/management/impersonate?${OPT_OUT_PARAM}=1`
      : `/?${OPT_OUT_PARAM}=1`;

    const session = getStoredSession();
    const target = session
      ? await buildOldFrontendUrl(OLD_FRONTEND_URL, path, session)
      : `${OLD_FRONTEND_URL}${path}`;
    window.location.assign(target);
  }

  function handleDismiss() {
    // No navigation here, so no flush delay — the document stays alive.
    if (tracked) trackAction(DISMISS_ACTION, trackedContext());
    setOldDashboardBannerDismissed(true);
  }

  return { visible, leaving, handleLeave, handleDismiss };
}
